import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'
import LoadingSpinner from '../../../components/LoadingSpinner'
import MemberBottomNav from '../../../components/MemberBottomNav'
import { 
  ArrowLeft,
  CalendarBlank,
  MapPin
} from '@phosphor-icons/react'

export default function EventDetails() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [event, setEvent] = useState(null)
  const [eventStatus, setEventStatus] = useState(null)
  const [eventCategory, setEventCategory] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadEventDetails()
  }, [id])

  const loadEventDetails = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()

      const { data: eventData } = await supabase
        .from('events')
        .select('*')
        .eq('id', id)
        .single()

      if (!eventData) {
        navigate('/member/events')
        return
      }

      setEvent(eventData)

      // Determine event category
      const now = new Date()
      const todayDate = now.toISOString().split('T')[0]
      const currentTime = now.toTimeString().slice(0, 5)
      const eventDate = eventData.start_date
      const startTime = eventData.start_time
      const endTime = eventData.end_time

      let category = 'past'
      
      if (eventDate > todayDate) {
        category = 'upcoming'
      } else if (eventDate === todayDate) {
        if (startTime > currentTime) {
          category = 'upcoming'
        } else if (endTime > currentTime) {
          category = 'active'
        } else {
          category = 'past'
        }
      }

      setEventCategory(category)

      // Check attendance
      const { data: attendance } = await supabase
        .from('attendance_records')
        .select('*')
        .eq('event_id', id)
        .eq('user_id', user.id)
        .maybeSingle()

      if (attendance) {
        setEventStatus('attended')
      } else {
        const { data: confirmation } = await supabase
          .from('event_confirmations')
          .select('status')
          .eq('event_id', id)
          .eq('user_id', user.id)
          .maybeSingle()

        if (category === 'past') {
          // For past events: if not attended, it's either missed or declined
          if (confirmation?.status === 'declined') {
            setEventStatus('declined')
          } else {
            setEventStatus('missed')
          }
        } else {
          // For upcoming/active events: use confirmation status
          if (confirmation?.status === 'confirmed') {
            setEventStatus('confirmed')
          } else if (confirmation?.status === 'declined') {
            setEventStatus('declined')
          } else {
            setEventStatus('pending')
          }
        }
      }

    } catch (error) {
      console.error('Load event details error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirm = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      await supabase
        .from('event_confirmations')
        .upsert({
          event_id: id,
          user_id: user.id,
          status: 'confirmed',
          confirmed_at: new Date().toISOString()
        }, {
          onConflict: 'event_id,user_id'
        })

      setEventStatus('confirmed')
    } catch (error) {
      console.error('Confirm event error:', error)
    }
  }

  const handleDecline = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      await supabase
        .from('event_confirmations')
        .upsert({
          event_id: id,
          user_id: user.id,
          status: 'declined',
          confirmed_at: new Date().toISOString()
        }, {
          onConflict: 'event_id,user_id'
        })

      setEventStatus('declined')
    } catch (error) {
      console.error('Decline event error:', error)
    }
  }

  const getStatusBadge = () => {
    if (eventStatus === 'attended') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-normal bg-[#EAF5ED] text-[#2B9646] outline outline-1 outline-[#BFE0C8]">
          Attended
        </span>
      )
    }
    if (eventStatus === 'confirmed') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-normal bg-[#EAF5ED] text-[#2B9646] outline outline-1 outline-[#BFE0C8]">
          Confirmed
        </span>
      )
    }
    if (eventStatus === 'declined') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-normal bg-[#FEF0EB] text-[#A94C25] outline outline-1 outline-[#FBD3C2]">
          Declined
        </span>
      )
    }
    if (eventStatus === 'missed') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-normal bg-[#FEF0EB] text-[#A94C25] outline outline-1 outline-[#FBD3C2]">
          Missed
        </span>
      )
    }
    return (
      <span className="px-2.5 py-1 rounded-full text-xs font-normal bg-slate-50 text-slate-500 outline outline-1 outline-[#E2E8F0]">
        Pending
      </span>
    )
  }

  if (loading) {
    return <LoadingSpinner message="Loading event details..." />
  }

  if (!event) {
    return null
  }

  const isVirtual = event.event_type === 'virtual'

  return (
    <>
      <div className="bg-white min-h-screen pb-20">
        {/* Header */}
        <div className="bg-white px-6 pt-10 pb-4">
          <div className="flex items-center relative">
            <button
              onClick={() => navigate(-1)}
              className="bg-slate-50 border border-slate-200 rounded-[20px] p-2"
            >
              <ArrowLeft size={20} weight="regular" className="text-slate-600" />
            </button>
            <h1 className="text-xl font-semibold text-slate-800 absolute left-1/2 transform -translate-x-1/2">
              Event details
            </h1>
          </div>
        </div>

        {/* Content */}
        <div className="px-6">
          {/* Banner Image */}
          {event.banner_url ? (
            <img 
              src={event.banner_url} 
              alt={event.title}
              className="w-full h-[367px] object-cover rounded-[20px] mb-4"
            />
          ) : (
            <div className="w-full h-[367px] bg-gradient-to-br from-purple-500 via-purple-600 to-orange-400 rounded-[20px] mb-4 flex items-center justify-center">
              <CalendarBlank size={64} weight="light" className="text-white opacity-50" />
            </div>
          )}

          {/* Title */}
          <h2 className="text-3xl font-semibold text-[#1E293B] mb-1 leading-tight">{event.title}</h2>
          
          {/* Date and Time - Single line */}
          <p className="text-base text-[#475569] mb-3 font-normal">
            {new Date(event.start_date).toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric', 
              year: 'numeric' 
            })} · {event.start_time}
          </p>

          {/* Status and Type badges */}
          <div className="flex items-center gap-2 mb-6">
            {getStatusBadge()}
            <span className="px-2.5 py-1 rounded-full text-sm font-normal bg-[#EAF0FF] text-[#0F1F4C]">
              {isVirtual ? 'Virtual event' : 'Physical event'}
            </span>
          </div>

          {/* Venue / Link Section */}
          <div className="mb-6">
            <p className="text-sm text-[#64748B] mb-1 font-normal">Venue / Link</p>
             <div className="border-t border-[#E2E8F0] mt-1 mb-2"></div>
            <div className="flex items-center gap-2">
              <MapPin size={16} weight="fill" className="text-slate-500 flex-shrink-0" />
              {isVirtual && event.meeting_link ? (
                <a 
                  href={event.meeting_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-base text-[#3167fe] font-normal hover:underline"
                >
                  {event.meeting_link}
                </a>
              ) : (
                <span className="text-base text-[#1E293B] font-normal">
                  {isVirtual ? (event.venue || 'No link provided') : (event.venue || 'No venue')}
                </span>
              )}
            </div>
          </div>

          {/* Description Section */}
          <div className="mb-14">
            <p className="text-sm text-[#64748B] mb-1 font-normal">Description</p>
            <div className="border-t border-slate-200 mt-1 mb-2"></div>
            <p className="text-base text-[#1E293B] leading-relaxed">
              {event.description || 'No description'}
            </p>
          </div>

          {/* Action buttons - for upcoming and active events */}
          {(eventCategory === 'upcoming' || eventCategory === 'active') && eventStatus !== 'attended' && (
            <div className="mb-4">
              {eventStatus === 'confirmed' ? (
                <button
                  onClick={handleDecline}
                  className="w-full px-4 py-2 rounded-[10px] text-base font-normal text-slate-700 bg-white border-2 border-slate-300 hover:bg-slate-50 focus:outline-none focus:border-slate-400 active:border-slate-400 transition-colors"
                >
                  Change to decline
                </button>
              ) : eventStatus === 'declined' ? (
                <button
                  onClick={handleConfirm}
                  className="w-full px-4 py-2 rounded-[10px] text-base font-normal text-white bg-[#3167fe] hover:bg-[#2557ed] focus:outline-none transition-colors"
                >
                  Change to confirm
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={handleConfirm}
                    className="flex-1 bg-[#3167fe] text-white px-4 py-2 rounded-[10px] text-base font-normal hover:bg-[#2557ed] transition-colors"
                  >
                    Confirm attendance
                  </button>
                  <button
                    onClick={handleDecline}
                    className="px-4 py-2 rounded-[10px] text-base font-normal text-slate-500 bg-[#f2f4f6] hover:bg-slate-200 transition-colors"
                  >
                    Decline
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Event ended text - only for past events */}
          {eventCategory === 'past' && (
            <p className="text-center text-xs text-slate-500 mt-6">
              Event ended at {event.end_time}
            </p>
          )}
        </div>
      </div>
    </>
  )
}